import { useMemo } from "react"

import { useEvent } from "shared/lib/useEvent"
import {
  CreateDimConfigurationInput,
  DimConfiguration
} from "shared/types/__generated__/graphql"

import {
  useCreateDimConfigurationMutation,
  useDeleteDimConfigurationMutation,
  useUpdateDimConfigurationMutation
} from "entities/dim-configuration/graphql/mutations.generated"
import { useDimConfigurationsQuery } from "entities/dim-configuration/graphql/queries.generated"

export function useDimConfigurationAdminPageModel() {
  const { data, loading, error, refetch } = useDimConfigurationsQuery()

  const [createMutation, { loading: isCreating }] =
    useCreateDimConfigurationMutation()
  const [updateMutation, { loading: isUpdating }] =
    useUpdateDimConfigurationMutation()
  const [deleteMutation, { loading: isDeleting }] =
    useDeleteDimConfigurationMutation()

  const configurations = useMemo(() => {
    const configs = data?.dimConfigurations ?? []
    return [...configs].sort((a, b) =>
      a.productName.localeCompare(b.productName)
    )
  }, [data?.dimConfigurations])

  const createConfiguration = useEvent(
    async (input: CreateDimConfigurationInput) => {
      await createMutation({
        variables: { input }
      })
      await refetch()
    }
  )

  const updateConfiguration = useEvent(
    async (
      id: string,
      input: {
        productName?: string
        productType?: string
        dimensioningKey?: string
        isActive?: boolean
      }
    ) => {
      await updateMutation({
        variables: { id, input }
      })
      await refetch()
    }
  )

  const deleteConfiguration = useEvent(async (id: string) => {
    await deleteMutation({
      variables: { id }
    })
    await refetch()
  })

  const onChange = useEvent(async (config: DimConfiguration) => {
    await updateConfiguration(config.id, {
      productName: config.productName,
      productType: config.productType,
      dimensioningKey: config.dimensioningKey ?? undefined,
      isActive: config.isActive ?? undefined
    })
  })

  return {
    configurations,
    isLoading: loading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    createConfiguration,
    deleteConfiguration,
    onChange
  }
}
